// ---------------------------------------------------------------------------
// Thumbs.
//
// The keyboard controls are a tiller and a space bar, neither of which a phone
// has. On a coarse pointer the helm appears as two buttons and the hint stops
// being a caption about the space bar and becomes the button itself.
// ---------------------------------------------------------------------------

/** True on a device whose main pointer is a finger. */
export function isTouch() {
    return window.matchMedia?.('(pointer: coarse)').matches
        || navigator.maxTouchPoints > 0;
}

/**
 * @param root
 * @param onTurn   (-1 | 0 | 1) => void
 * @param onAction () => void — what the space bar would have done
 */
export function setupTouch(root, { onTurn, onAction }) {
    if (!isTouch()) return false;
    root.body.classList.add('is-touch');

    const helm = root.querySelector('#helm');
    if (helm) {
        helm.hidden = false;
        for (const key of helm.querySelectorAll('.helm-key')) {
            const turn = Number(key.dataset.turn);
            // Pointer events rather than touch ones: a finger sliding off the
            // button still ends the turn, which a touchend alone would miss.
            const press = (e) => {
                e.preventDefault();
                key.setPointerCapture?.(e.pointerId);
                onTurn(turn);
            };
            const release = (e) => {
                e.preventDefault();
                onTurn(0);
            };
            key.addEventListener('pointerdown', press);
            key.addEventListener('pointerup', release);
            key.addEventListener('pointercancel', release);
            key.addEventListener('lostpointercapture', release);
        }
    }

    const hint = root.querySelector('#fish-hint');
    if (hint) {
        hint.addEventListener('click', (e) => {
            e.preventDefault();
            onAction();
        });
    }

    return true;
}

/**
 * Wire the fullscreen button, where there is anything to wire.
 *
 * iPhone Safari has no fullscreen API for anything that is not a video, so
 * there the button stays hidden and Add to Home Screen is the only way to lose
 * the browser chrome — which is what the manifest is for.
 */
export function setupFullscreen(root) {
    const button = root.querySelector('#fullscreen');
    if (!button) return false;
    const el = root.documentElement;
    if (!el.requestFullscreen && !el.webkitRequestFullscreen) return false;

    button.hidden = false;
    const sync = () => {
        const on = Boolean(root.fullscreenElement || root.webkitFullscreenElement);
        button.setAttribute('aria-pressed', String(on));
        button.title = on ? 'Leave fullscreen' : 'Fullscreen';
        button.setAttribute('aria-label', button.title);
    };

    button.addEventListener('click', () => {
        const on = Boolean(root.fullscreenElement || root.webkitFullscreenElement);
        const call = on
            ? (root.exitFullscreen || root.webkitExitFullscreen)?.bind(root)
            : (el.requestFullscreen || el.webkitRequestFullscreen)?.bind(el);
        // Rejected when the gesture is not trusted, which is not worth a crash.
        Promise.resolve(call?.()).catch(() => {}).finally(sync);
    });
    root.addEventListener('fullscreenchange', sync);
    root.addEventListener('webkitfullscreenchange', sync);
    return true;
}
